"use client"

import styled from "@emotion/styled"
import { Box, Button, IconButton } from "@mui/material"
import { useRef, useState } from "react"
import { extractProperty, getNonUmbracoUrl, getUmbracoMediaUrl } from "utils"
import { useIsDesktop } from "./common/bit/MediaQueries"
import { ResponsiveMedia, isVideo } from "./common/bit/MediaRenderer"
import { PauseIcon, Play2Icon } from "./icons"
import WhatsAppIcon from "./icons/WhatsAppIcon"
import NewText from "./NewText/NewText"
import PortContainer from "./PortContainer"
import PortPrimaryButton from "./PortPrimaryButton"

const StyledPortHeroBanner = styled(Box)<{ $innerPage?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 100%;
  height: ${(props) => (props.$innerPage ? "508px" : "100vh")};
  height: ${(props) => (props.$innerPage ? "508px" : "100dvh")};

  position: relative;
  ${(props) => props.theme.breakpoints.up("xl")} {
    height: ${(props) => (props.$innerPage ? "762px" : "100vh")};
    height: ${(props) => (props.$innerPage ? "762px" : "100dvh")};
  }
`

const StyledContent = styled(PortContainer)`
  width: 100%;
  height: 100%;
  padding-top: 120px;
  padding-bottom: 40px;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 0;
  text-align: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 120px;
    padding-bottom: 60px;
  }
`

const StyledMedia = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  & > div {
    width: 100%;
    height: 100%;
  }
  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: "";
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    background-image: linear-gradient(180deg, rgba(2, 10, 13, 0) 0%, rgba(2, 10, 13, 0.8) 100%);
  }
`

const ControlButton = styled(IconButton)<{ $isPlaying: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0;
  background-color: rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease-out;
  color: white;
  & svg {
    color: inherit;
  }
  &:hover {
    background-color: ${({ $isPlaying }) => (!$isPlaying ? "white" : "rgba(255, 255, 255, 0.5)")};
    color: ${({ $isPlaying }) => (!$isPlaying ? "black" : "")};
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 40px;
    height: 40px;
  }
`

const StyledWhatsAppButton = styled(Button)`
  position: absolute;
  top: 88px;
  inset-inline-end: 20px;

  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  min-width: unset;

  transition: all 0.3s ease-out;
  border: 1px solid white;
  background-color: rgba(255, 255, 255, 0.8);

  /* Drop Shadow */
  box-shadow: 0 0 17.3px 0 rgba(114, 111, 111, 0.2);

  svg {
    width: 26px;
    height: 26px;
  }
  &:hover {
    background-color: white;
    box-shadow: 0 0 20px 0 rgba(114, 111, 111, 0.4);
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 60px;
    height: 60px;
    top: 113px;
    svg {
      width: 36px;
      height: 36px;
    }
  }

  ${(props) => props.theme.breakpoints.up(1200)} {
    inset-inline-end: 20px;
  }
`

type PortHeroBannerProps = {
  data: any
  innerPage?: boolean
}

const resolveMediaUrl = (mediaValue: any) => {
  if (typeof mediaValue === "string" && mediaValue.length) {
    return getUmbracoMediaUrl(mediaValue)
  }
  if (mediaValue && typeof mediaValue === "object") {
    const maybeUrl = (mediaValue as { url?: string }).url
    if (typeof maybeUrl === "string" && maybeUrl.length) {
      return getUmbracoMediaUrl(maybeUrl)
    }
  }
  return undefined
}

const PortHeroBanner = ({ data, innerPage }: PortHeroBannerProps) => {
  const desktopMedia = resolveMediaUrl(extractProperty(data, "desktopMedia"))
  const mobileMedia = resolveMediaUrl(extractProperty(data, "mobileMedia"))
  const title = extractProperty(data, "title")
  const subtitle = extractProperty(data, "subtitle")
  const button = extractProperty(data, "button")?.[0]
  const secondButton = extractProperty(data, "secondButton")?.[0]
  const text = extractProperty(data, "text")
  const whatsAppIcon = extractProperty(data, "whatsAppIcon")
  const whatsAppLink = extractProperty(data, "whatsAppLink")?.[0]

  const altText = typeof title === "string" && title.length ? title : "Port Hero Banner"

  const isDesktop = useIsDesktop()
  const desktopVideoRef = useRef<HTMLVideoElement>(null)
  const mobileVideoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const hasVideoMedia = (isDesktop && isVideo(desktopMedia)) || (!isDesktop && isVideo(mobileMedia))

  const syncPlaybackState = (shouldPlay: boolean) => {
    // eslint-disable-next-line no-extra-semi
    ;[desktopVideoRef.current, mobileVideoRef.current].forEach((video) => {
      if (!video) return
      if (shouldPlay) {
        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => undefined)
        }
      } else {
        video.pause()
      }
    })
  }

  const handlePlaybackToggle = () => {
    setIsPlaying((prev) => {
      const next = !prev
      syncPlaybackState(next)
      return next
    })
  }

  // in case data fields are empty, return null
  if (!desktopMedia && !mobileMedia && !title && !subtitle) {
    return null
  }

  return (
    <StyledPortHeroBanner component="section" $innerPage={!!innerPage}>
      <StyledMedia>
        <ResponsiveMedia
          desktopMedia={desktopMedia}
          mobileMedia={mobileMedia}
          alt={altText}
          priority
          desktopVideoRef={desktopVideoRef}
          mobileVideoRef={mobileVideoRef}
        />
      </StyledMedia>
      <StyledContent
        paddingInline={16}
        maxWidthXL={1440}
        sx={{
          height: "100%",
          position: "relative",
          zIndex: 2,
          paddingTop: { xs: "120px" },
          paddingBottom: { xs: "40px", md: "60px" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          alignItems: {
            xs: "center",
            md: innerPage ? "flex-start" : "center",
          },
          textAlign: {
            xs: "center",
            md: innerPage ? "left" : "center",
          },
        }}
      >
        {whatsAppIcon && whatsAppLink?.url && (
          <StyledWhatsAppButton
            href={getNonUmbracoUrl(whatsAppLink.url)}
            aria-label="WhatsApp"
            rel="noreferrer"
            // @ts-ignore
            target={whatsAppLink.target || "_self"}
          >
            <WhatsAppIcon />
          </StyledWhatsAppButton>
        )}
        {title && (
          <NewText
            dir="auto"
            variant="h1"
            component={"h1"}
            desktopFontWeight={700}
            desktopFontSize={96}
            mobileFontSize={60}
            desktopLineHeight={"120%"}
            mobileLineHeight={"120%"}
            sx={{
              color: {
                xs: "#fff !important",
              },
              marginBottom: {
                xs: "8px",
              },
              letterSpacing: {
                xs: "1px",
                md: "2px",
              },
            }}
          >
            {title}
          </NewText>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            width: "100%",
            marginBottom: {
              xs: innerPage ? "16px" : "24px",
              md: "32px",
            },
          }}
        >
          {subtitle && (
            <NewText
              variant="h2"
              component={"h2"}
              desktopFontWeight={400}
              desktopFontSize={40}
              mobileFontSize={30}
              desktopLineHeight={"125%"}
              mobileLineHeight={"145%"}
              textTransform={{
                xs: "capitalize",
              }}
              sx={{
                color: {
                  xs: "#fff !important",
                },
                letterSpacing: {
                  xs: "-0.6px",
                  md: "-0.8px",
                },
              }}
            >
              {subtitle}
            </NewText>
          )}
          {!!innerPage && text && (
            <NewText
              mobileFontSize={20}
              desktopFontSize={24}
              mobileFontWeight={400}
              desktopFontWeight={400}
              desktopLineHeight={"150%"}
              mobileLineHeight={"150%"}
              letterSpacing={{
                xs: "-0.6px",
                md: "unset",
              }}
              sx={{
                color: {
                  xs: "#fff !important",
                },
              }}
              variant="body1"
              component={"p"}
            >
              {text}
            </NewText>
          )}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
            alignItems: "flex-end",
            justifyItems: "center",
            width: "100%",
            gap: { xs: "12px", md: 0 },
          }}
        >
          <Box
            sx={{
              display: {
                xs: "none",
                md: !innerPage ? "block" : "none",
              },
              width: {
                xs: "40px",
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              gap: { xs: "12px", md: "16px" },
              justifySelf: "center",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <PortPrimaryButton
              button={button}
              sx={{
                minWidth: {
                  xs: "103px",
                  md: "200px",
                },
                maxWidth: "max-content",
                height: {
                  xs: "37px",
                  md: "46px",
                },
              }}
            />
            <PortPrimaryButton
              button={secondButton}
              sx={{
                minWidth: {
                  xs: "103px",
                  md: "200px",
                },
                maxWidth: "max-content",
                height: {
                  xs: "37px",
                  md: "46px",
                },
                backgroundColor: "transparent",
                border: "1px solid #fff",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  border: "1px solid #fff",
                },
              }}
            />
          </Box>
          <Box
            sx={{
              display: {
                xs: "none",
                md: innerPage ? "block" : "none",
              },
              width: {
                xs: "40px",
              },
            }}
          />
          <Box>
            {hasVideoMedia && (
              <ControlButton
                aria-label={isPlaying ? "Pause background video" : "Play background video"}
                onClick={handlePlaybackToggle}
                disableRipple
                $isPlaying={isPlaying}
              >
                {isPlaying ? <PauseIcon /> : <Play2Icon />}
              </ControlButton>
            )}
          </Box>
        </Box>
      </StyledContent>
    </StyledPortHeroBanner>
  )
}
export default PortHeroBanner
